import styles from './ERModel.module.css';
import React, { useCallback, useEffect, useState } from 'react';
import {
  addEdge,
  Background,
  ConnectionMode,
  ControlButton,
  Controls,
  MiniMap,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import Icon from 'components/ui/common/Icon';
import UUID from 'bng/utils/UUID';

function deriveEdgesFromJoins(joins = [], nodes = []) {
  const chooseHandle = (nodeA, nodeB, field, side = 'source') => {
    const ax = nodeA.position.x + nodeA.width / 2;
    const bx = nodeB.position.x + nodeB.width / 2;
    const sideName = ax < bx ? 'right' : 'left';
    return `${side}-${sideName}-${nodeA.id}-field-${field}`;
  };

  const edgesById = new Map();

  joins.forEach((j) => {
    const sourceNode = nodes.find((n) => n.id === j.sourceNodeId);
    const targetNode = nodes.find((n) => n.id === j.targetNodeId);
    if (!sourceNode || !targetNode) return;

    const id = `join-${j.id}`;
    edgesById.set(id, {
      id,
      source: sourceNode.id,
      target: targetNode.id,
      sourceHandle: chooseHandle(sourceNode, targetNode, j.sourceField, 'source'),
      targetHandle: chooseHandle(targetNode, sourceNode, j.targetField, 'target'),
      type: 'databaseEdge',
      style: { strokeWidth: 2, stroke: '#005dff' },
    });
  });

  return Array.from(edgesById.values());
}

export const NODE_TYPES = { customNode: 'customNode', databaseTable: 'databaseTable' };

export default function ERModel({
  className = '',
  initialNodes = [],
  showControls = false,
  showMinimap = false,
  nodeTypes = {},
  edgeTypes = {},
  onNewNode = null,
  onNewEdge = null,
  showNewNodeButton = false,
  onSelectNode = _.noop,
  onChange = _.noop,
  children,
  joins,
  onAddTable,
}) {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const onConnect = useCallback((params) => setEdges((eds) => addEdge(params, eds)), [setEdges]);

  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes]);

  useEffect(() => {
    setEdges(deriveEdgesFromJoins(joins, nodes));
  }, [joins, nodes]);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  const onDrop = useCallback(
    (event) => {
      try {
        event.preventDefault();
        const data = JSON.parse(event.dataTransfer.getData('application/reactflow'));

        const position = reactFlowInstance.screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });

        onAddTable(data, position)

      } catch (e) {
        console.error('Error on onDrop: ', e);
      }
    },
    [reactFlowInstance, onAddTable]
  );

  const defaultEdgeOptions = {
    type: 'databaseEdge',
    style: { strokeWidth: 2, stroke: '#005dff' },
  };

  return (
    <div className={`ERModel ${styles.ERModel} ${className}`}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        minZoom={0.5}
        maxZoom={2}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={(params) =>
          onNewEdge
            ? onNewEdge({
                params,
                nodes,
                setNodes,
                edges,
                setEdges,
                reactFlowInstance,
              })
            : onConnect(params)
        }
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        connectionMode={ConnectionMode.Loose}
        onNodeClick={onSelectNode}
        onInit={(instance) => {
          setReactFlowInstance(instance);
        }}
        onNodeDragStop={(event, node) => {
          const updatedNodes = nodes.map((n) => (n.id === node.id ? { ...n, position: node.position } : n));
          onChange({ nodes: updatedNodes, joins });
        }}
        onDragOver={onDragOver}
        onDrop={onDrop}
        defaultEdgeOptions={defaultEdgeOptions}
      >
        {showControls && (
          <Controls position={'top-right'} showInteractive={false}>
            {showNewNodeButton && (
              <ControlButton
                onClick={() => {
                  if (onNewNode) {
                    onNewNode({ nodes, setNodes, setEdges, reactFlowInstance });
                  } else {
                    const newNode = {
                      id: UUID.generate(),
                      type: 'Default',
                      position: { x: 0, y: nodes.length * 100 },
                      data: { label: nodes.length.toString() },
                    };
                    setNodes((nds) => nds.concat(newNode));
                  }
                }}
              >
                <Icon icon={'note_add'} size={20} className={styles.controlsIcon} />
              </ControlButton>
            )}
          </Controls>
        )}
        {showMinimap && <MiniMap />}
        <Background variant="dots" gap={12} size={1} />
        {_.isFunction(children) ? children({ nodes, setNodes, edges, setEdges, ...reactFlowInstance }) : children}
      </ReactFlow>
    </div>
  );
}
